import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Spin,
  notification,
} from "antd";
import Title from "antd/es/typography/Title";
import { useData } from "../../contexts/DataContext";
import {
  fetchContactFormFields,
  fetchInquirePage,
  sendUserInquiry,
} from "../../services/ApiService";
import { ContactFormField } from "../../models/ContactFormField";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import FAQs from "../../components/FAQs";
import { InquiryFormData } from "../../models/InquiryFormData";

const { Option } = Select;

const Inquire: React.FC = () => {
  const [form] = Form.useForm();
  const [contactPreference, setContactPreference] = useState("Text");
  const [formDisabled, setFormDisabled] = useState(false); // State variable to track form disabled state
  const { state, dispatch } = useData();

  useEffect(() => {
    if (!state.inquirePage) {
      fetchInquirePage(dispatch);
    }
    if (!state.contactFormFields) {
      fetchContactFormFields(dispatch);
    }
  }, [dispatch, state.contactFormFields, state.inquirePage]);

  const onFinish = (values: any) => {
    setFormDisabled(true); // Disable the form when submitting
    const formData: InquiryFormData = {
      Name: values.Name,
      InquiryType: values.InquiryType,
      ContactPreference: values.ContactPreference,
      RequesterContactDetails: values.RequesterContactDetails,
      Message: values.Message,
    };

    const contactPreferenceField = state.contactFormFields?.find(
      (formField: ContactFormField) =>
        formField.FormID === "ContactPreference"
    );
    if (
      contactPreferenceField?.Type === "Dropdown" &&
      values.ContactPreference
    ) {
      formData.ContactPreference =
        contactPreferenceField.DropdownOptions?.Options[
          values.ContactPreference
        ]!;
    }
    const InquiryTypeField = state.contactFormFields?.find(
      (formField: ContactFormField) =>
        formField.FormID === "InquiryType"
    );
    if (
      InquiryTypeField?.Type === "Dropdown" &&
      values.InquiryType
    ) {
      formData.InquiryType =
        InquiryTypeField.DropdownOptions?.Options[
          values.InquiryType
        ]!;
    }

    sendUserInquiry(formData).then((response) => {
      if (response === true) {
        notification.success({
          message: "Inquiry submitted!",
          description: `Thank you for reaching out!`,
          placement: state.isMobile ? "bottom" : "topRight",
        });
      } else {
        notification.warning({
          message: "Inquiry failed to be submitted!",
          description: `Thank you for reaching out!`,
          placement: state.isMobile ? "bottom" : "topRight",
        });

        setFormDisabled(false); // Re-enable the form after receiving the response
      }
    });
  };

  const renderFormFields = () => {
    return state.contactFormFields?.map((formField: ContactFormField) => {
      const { Type, Label, FormID, PlaceholderText, DropdownOptions } =
        formField;
      switch (Type) {
        case "Text":
          return (
            <Form.Item
              key={FormID}
              label={Label}
              name={FormID}
              rules={[
                { required: true, message: `Please input your ${Label}!` },
              ]}
            >
              <Input placeholder={PlaceholderText} />
            </Form.Item>
          );
        case "Textarea":
          return (
            <Form.Item
              key={FormID}
              label={Label}
              name={FormID}
              rules={[
                { required: true, message: `Please input your ${Label}!` },
              ]}
            >
              <Input.TextArea rows={4} placeholder={PlaceholderText} />
            </Form.Item>
          );
        case "Dropdown":
          const defaultOption = Object.keys(DropdownOptions?.Options ?? {})[0];
          return (
            <Form.Item
              key={FormID}
              label={Label}
              name={FormID}
              initialValue={defaultOption}
              rules={[
                { required: true, message: `Please select your ${Label}!` },
              ]}
            >
              <Select
                placeholder={PlaceholderText}
                onChange={(value) => {
                  if (FormID === "ContactPreference") {
                    setContactPreference(DropdownOptions?.Options[value]!);
                  }
                }}
              >
                {DropdownOptions?.Options &&
                  Object.entries(DropdownOptions.Options).map(
                    ([key, value]) => (
                      <Option key={key} value={key}>
                        {value}
                      </Option>
                    )
                  )}
              </Select>
            </Form.Item>
          );

        default:
          return null;
      }
    });
  };

  return (
    <div>
      <div
        style={{
          marginBottom: "20px",
          marginLeft: state.isMobile ? "50px" : "40px",
          marginRight: state.isMobile ? "50px" : "40px",
        }}
      >
        <Row justify="center">
          <Title level={2}>
            {state.inquirePage ? (
              <BlocksRenderer
                content={state.inquirePage.Title}
              ></BlocksRenderer>
            ) : (
              "Contact us"
            )}
          </Title>
        </Row>
        <Row justify="center" style={{ marginBottom: "15px" }}>
          {state.inquirePage ? (
            <BlocksRenderer
              content={state.inquirePage.Text}
            ></BlocksRenderer>
          ) : (
            <div>
              Please fill out this contact form and we will get back to you
              soon! Thanks!
            </div>
          )}
        </Row>
        {state.contactFormFields ? (
          <Form
            form={form}
            onFinish={onFinish}
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 14 }}
            disabled={formDisabled} // Pass the formDisabled state to disable the form
            style={{ justifyContent: "center" }}
          >
            {renderFormFields()}

            <Form.Item
              label={
                contactPreference === "Text" ||
                contactPreference === "Phone Call"
                  ? "Phone Number"
                  : "Email"
              }
              name="RequesterContactDetails"
              rules={
                contactPreference === "Text" ||
                contactPreference === "Phone Call"
                  ? [
                      {
                        required: true,
                        message: "Please input your phone number!",
                      },
                    ]
                  : [
                      {
                        required: true,
                        message: "Please input your email address!",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email address!",
                      },
                    ]
              }
            >
              <Input
                placeholder={
                  contactPreference === "Text" ||
                  contactPreference === "Phone Call"
                    ? "Your Phone Number..."
                    : "Your Email Address..."
                }
              />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: state.isMobile ? 0 : 6, span: state.isMobile ? 24 : 14 }} style={{ textAlign: "center" }}>
              <Button type="primary" htmlType="submit" block>
                Submit
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Spin></Spin>
        )}
      </div>

      <Row justify="center">
        <FAQs modal={false} />
      </Row>
    </div>
  );
};

export default Inquire;
